/**
 * ComplaintsSettings — Portal Settings → Complaints tab.
 * Admin-editable: max response days (deadline hard cap), ack action lead
 * time, topic list, acknowledgement email template.
 */
import { useState, useEffect } from "react";
import { Loader2, Megaphone, Save, Trash2, Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getComplaintsSettings, updateComplaintsSettings } from "@/lib/api";

export default function ComplaintsSettings() {
  const [settings, setSettings] = useState(null);
  const [newTopic, setNewTopic] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getComplaintsSettings().then(setSettings)
      .catch(e => toast.error("Failed to load complaints settings: " + (e?.response?.data?.detail || e.message)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateComplaintsSettings({
        max_response_days: Number(settings.max_response_days) || 30,
        ack_due_days: Number(settings.ack_due_days) || 2,
        topics: settings.topics,
        ack_template: settings.ack_template,
      });
      setSettings(r);
      toast.success("Complaints settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-rose-600" /> Complaints workflow
          </CardTitle>
          <CardDescription className="text-xs">
            Deadlines and topics used by the Complaints register (Collections & Legal).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600">Max final-response days (hard cap)</label>
              <Input type="number" min={1} max={90} value={settings.max_response_days}
                onChange={(e) => setSettings(s => ({ ...s, max_response_days: e.target.value }))}
                className="h-9 w-28" data-testid="complaints-max-days-input" />
              <div className="text-[11px] text-slate-400 mt-1">The response deadline can't be set later than this many days after the complaint was received.</div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Acknowledgement action due (days)</label>
              <Input type="number" min={1} max={14} value={settings.ack_due_days}
                onChange={(e) => setSettings(s => ({ ...s, ack_due_days: e.target.value }))}
                className="h-9 w-28" data-testid="complaints-ack-days-input" />
              <div className="text-[11px] text-slate-400 mt-1">The Legal Calendar “Complaint acknowledgement” action is diarised this many days after logging.</div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Complaint topics</label>
            <div className="space-y-1.5 mt-1">
              {settings.topics.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={t}
                    onChange={(e) => setSettings(s => {
                      const topics = [...s.topics]; topics[i] = e.target.value; return { ...s, topics };
                    })}
                    className="h-8 text-xs" data-testid={`complaints-topic-input-${i}`} />
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-rose-500"
                    onClick={() => setSettings(s => ({ ...s, topics: s.topics.filter((_, j) => j !== i) }))}
                    data-testid={`complaints-topic-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && newTopic.trim() && (setSettings(s => ({ ...s, topics: [...s.topics, newTopic.trim()] })), setNewTopic(""))}
                  placeholder="Add a topic…" className="h-8 text-xs" data-testid="complaints-topic-new" />
                <Button variant="outline" size="sm" className="h-8 text-xs"
                  disabled={!newTopic.trim()}
                  onClick={() => { setSettings(s => ({ ...s, topics: [...s.topics, newTopic.trim()] })); setNewTopic(""); }}
                  data-testid="complaints-topic-add">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Acknowledgement email template</label>
            <Textarea value={settings.ack_template} rows={11}
              onChange={(e) => setSettings(s => ({ ...s, ack_template: e.target.value }))}
              className="text-xs font-mono mt-1" data-testid="complaints-ack-template" />
            <div className="text-[11px] text-slate-400 mt-1">
              Placeholders: {"{customer_name} {date_received} {topic} {response_due} {sf_number}"}
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} data-testid="complaints-settings-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
